/**
 * Plausibilitätsprüfung für das Geburtsdatum eines Schulkinds (#999).
 *
 * #997 hat nur einen groben Bereich abgesichert (30 Jahre rückwärts bis heute).
 * Die PROD-Auswertung vom 27.08.2026 zeigte, dass das nicht reicht: 24 von
 * 2.892 Kindern tragen ein Geburtsdatum, das nicht zu ihrem Jahrgang passt —
 * alle aus der Eltern-Anmeldung, keines je nachträglich korrigiert.
 *
 * Der Anker ist die Klasse, die direkt unter dem Geburtsdatum abgefragt wird:
 * Wer im Schuljahr `schoolYear` in Klasse `grade` startet, ist regulär im Jahr
 * `schoolYear - (grade - 1) - 6` geboren.
 */

/** Regelalter bei der Einschulung. */
const SCHOOL_ENTRY_AGE = 6;

/**
 * Zulässige Abweichung vom erwarteten Jahrgang, in Jahren.
 *
 * Bewusst weit: Rückstellung, vorzeitige Einschulung und Quereinsteiger aus
 * anderen Schulsystemen sind legitim und dürfen keine Warnung auslösen. Bei
 * allen 24 bekannten PROD-Fällen schlägt die Regel trotzdem an, weil dort die
 * Abweichung mindestens ein Jahrzehnt beträgt.
 */
export const BIRTH_YEAR_TOLERANCE = 2;

/**
 * Ältestes Alter, das ein Schulkind haben kann — Obergrenze für die harte
 * Datepicker-Untergrenze, die die Klasse nicht kennt.
 *
 * Hergeleitet am Extremfall: Klasse 4 im frühesten wählbaren Schuljahr (dem
 * Vorjahr), spät eingeschult und zweimal wiederholt, macht rund 13 Jahre. Die
 * 20 lassen sechs Jahre Puffer und blocken trotzdem jeden Erwachsenen-Jahrgang.
 *
 * `scripts/admin/find-implausible-birthdates.ts` hält denselben Wert.
 */
export const MAX_STUDENT_AGE_YEARS = 20;

/**
 * Erwartetes Geburtsjahr für ein Kind, das im Schuljahr `schoolYear` in der
 * Klasse `gradeSelection` startet.
 *
 * `gradeSelection` ist der Dropdown-Wert des Formulars: 1..4 = Klasse,
 * 0 = Vorklasse, PAKTKLASSE_FORM_VALUE (-1) = Paktklasse. Beide Sonderformen
 * liegen altersmässig um den Erstklässler-Jahrgang herum und werden deshalb
 * auf Klasse 1 abgebildet — die Toleranz deckt den Rest ab.
 */
export function expectedBirthYear(
  schoolYear: number,
  gradeSelection: number
): number {
  const grade = gradeSelection <= 0 ? 1 : gradeSelection;
  return schoolYear - (grade - 1) - SCHOOL_ENTRY_AGE;
}

/**
 * True, wenn das Geburtsjahr mehr als BIRTH_YEAR_TOLERANCE Jahre vom erwarteten
 * Jahrgang abweicht. Fehlt eine der Angaben, wird nicht gewarnt — die Prüfung
 * ergänzt die Pflichtfeld-Validierung, sie ersetzt sie nicht.
 */
export function isBirthDateImplausible(
  birthDate: Date | null | undefined,
  schoolYear: number | null | undefined,
  gradeSelection: number | null | undefined
): boolean {
  if (!isValidDate(birthDate) || schoolYear == null || gradeSelection == null) {
    return false;
  }
  const expected = expectedBirthYear(schoolYear, gradeSelection);
  return Math.abs(birthDate.getFullYear() - expected) > BIRTH_YEAR_TOLERANCE;
}

/**
 * Alter des Kindes zum Beginn des Schuljahres (01.08.).
 *
 * Gerechnet wird in lokaler Zeit gegen die lokalen Datumsanteile des
 * Geburtsdatums — das Ergebnis ist reine Anzeige, und der Nutzer liest sein
 * eingetipptes Datum ebenfalls lokal. Kann negativ sein, wenn ein Datum in der
 * Zukunft eingegeben wurde; genau das soll der Hinweis sichtbar machen.
 */
export function ageAtSchoolYearStart(
  birthDate: Date | null | undefined,
  schoolYear: number | null | undefined
): number | null {
  if (!isValidDate(birthDate) || schoolYear == null) {
    return null;
  }
  const start = new Date(schoolYear, 7, 1);
  let age = start.getFullYear() - birthDate.getFullYear();
  const monthDiff = start.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && start.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

function isValidDate(value: Date | null | undefined): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}
